import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Newspaper } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { api } from "@/lib/api";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

export default function Blog() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    api.get("/posts").then(({ data }) => setPosts(data)).catch(() => setPosts([]));
  }, []);

  return (
    <main>
      <PageHero
        eyebrow="Blog"
        titleLines={["Guide e consigli", "di informatica pratica."]}
        subtitle="Articoli, guide passo passo e consigli per imparare a usare il computer con serenità, scritti pensando a chi parte da zero."
      />

      <section className="mx-auto max-w-7xl px-5 py-12 pb-28 sm:px-8" data-testid="blog-list">
        {posts === null ? null : posts.length === 0 ? (
          <Reveal>
            <div className="rounded-3xl border border-[#D8E5E8] bg-[#EFF6F7] p-10 text-center" data-testid="blog-empty">
              <Newspaper className="mx-auto h-8 w-8 text-[#1F8296]" />
              <p className="mt-4 font-display text-xl font-semibold text-[#1A2E33]">Gli articoli sono in arrivo</p>
              <p className="mx-auto mt-2 max-w-md text-sm text-[#5C7178]">
                Stiamo preparando guide e consigli pratici di informatica. Torna presto a trovarci.
              </p>
            </div>
          </Reveal>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((p, i) => (
              <Reveal key={p.id} delay={0.06 * i}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-3xl border border-[#D8E5E8] bg-white shadow-[0_10px_40px_-28px_rgba(16,51,59,0.3)] transition-colors duration-500 hover:border-[#1F8296]/50"
                >
                  {p.featured_image_url && (
                    <div className="h-44 overflow-hidden">
                      <img
                        src={p.featured_image_url}
                        alt={p.title}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-6">
                    <p className="font-plex text-[11px] uppercase tracking-[0.2em] text-[#7A8F96]">{formatDate(p.created_at)}</p>
                    <h2 className="mt-2 font-display text-lg font-semibold leading-snug text-[#1A2E33]">{p.title}</h2>
                    {p.meta_description && (
                      <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-[#5C7178]">{p.meta_description}</p>
                    )}
                    <span className="mt-auto inline-flex items-center gap-1.5 pt-4 text-sm font-semibold text-[#1F8296] transition-colors group-hover:text-[#238496]">
                      Leggi l'articolo <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
